import React, { useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faExclamation,
  faCheck,
  faUpRightAndDownLeftFromCenter,
  faDownLeftAndUpRightToCenter,
  faUpload,
  faDownload,
  faCopy,
  faPaste,
} from '@fortawesome/free-solid-svg-icons'
import AceEditor from 'react-ace'
import 'brace/mode/json'
import 'brace/mode/html'
import 'brace/theme/ambiance'
import { Tooltip } from '@mui/material'
import clsx from 'clsx'
import style from './JsonEditor.module.css'
import { exportAsFile } from '../utils/export-as-file'

/**
 * The editor shell behind the code-valued config fields: an Ace editor at a
 * fixed height with an expand toggle, and a toolbar for save, upload, copy and
 * paste.
 *
 * Language-specific behaviour is passed in rather than branched on here.
 * `validate` and `format` are optional, and their buttons are absent when they
 * are: a JSON field can be checked and pretty-printed, an HTML page cannot be
 * usefully either, and a shell that pretended otherwise would show buttons that
 * do nothing.
 *
 * It borrows JsonEditor.module.css rather than owning a stylesheet, because the
 * two are the same chrome and duplicating it would let them drift.
 */
export type CodeTextEditorProps = {
  name?: string
  exportFileName?: string
  value: string
  onValueChange?: (value: string) => void
  readonly?: boolean
  /** An Ace mode whose `brace/mode/<mode>` module is imported above. */
  mode: 'json' | 'html'
  /** Offered when saving, and accepted when uploading. */
  fileExtension: string
  mimeType: string
  /** Height when collapsed; the expand toggle goes to 80vh either way. */
  collapsedHeight?: string
  /** Syntax check behind the "check" button. Returns 'ok' or the message to show. */
  validate?: (value: string) => 'ok' | string
  /** Reformats the value behind its own button. */
  format?: (value: string) => string
  formatLabel?: React.ReactNode
}

export function CodeTextEditor(props: CodeTextEditorProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [validationStatus, setValidationStatus] = useState<string | 'ok' | null>(null)

  const { validate, format } = props
  const editable = !props.readonly

  const check = () => {
    if (!validate) return
    setValidationStatus(validate(props.value))
  }

  const reformat = () => {
    if (!format) return
    props.onValueChange?.(format(props.value))
  }

  const save = () => {
    const filename = props.exportFileName || `${props.name || 'data'}.${props.fileExtension}`
    exportAsFile(props.value, filename, { type: props.mimeType })
  }

  const upload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target?.result
      if (text) props.onValueChange?.(text as string)
    }
    reader.readAsText(file)
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(props.value)
    } catch (err) {
      console.error(err)
      alert('Failed to copy')
    }
  }

  const paste = async () => {
    try {
      const text = await navigator.clipboard.readText()
      props.onValueChange?.(text)
    } catch (err) {
      console.error(err)
      alert('Failed to paste')
    }
  }

  return (
    <div className={style.wrapper}>
      <AceEditor
        key={String(expanded)}
        name={props.name}
        mode={props.mode}
        theme="ambiance"
        fontSize="12pt"
        style={{
          width: '100%',
          height: expanded ? '80vh' : (props.collapsedHeight ?? '120px'),
          fontFamily: 'Consolas, Inconsolata, Monaco, "Lucida Console", monospace',
          fontWeight: 500,
        }}
        value={props.value}
        readOnly={props.readonly}
        onChange={(value) => {
          if (props.readonly) return
          setValidationStatus(null)
          props.onValueChange?.(value)
        }}
      />
      <div className={style.bar}>
        {editable && validate && (
          <Tooltip title="Check syntax">
            <button className="px-1.5 py-px" type="button" onClick={check}>
              check
            </button>
          </Tooltip>
        )}
        {validationStatus && (
          <div className={clsx(style['json-status'], validationStatus !== 'ok' ? style.error : style.ok)}>
            <FontAwesomeIcon icon={validationStatus === 'ok' ? faCheck : faExclamation} />
            {validationStatus === 'ok' ? 'Valid' : String(validationStatus)}
          </div>
        )}
        <div className={style.separator} />
        {editable && format && (
          <Tooltip title="Format">
            <button className="px-1.5 py-px" type="button" onClick={reformat}>
              {props.formatLabel ?? <b>{`{ }`}</b>}
            </button>
          </Tooltip>
        )}
        <Tooltip title={`Save as .${props.fileExtension}`}>
          <button className="px-1.5 py-px" type="button" onClick={save}>
            <FontAwesomeIcon icon={faDownload} />
          </button>
        </Tooltip>
        {editable && (
          <Tooltip title={`Upload .${props.fileExtension}`}>
            <button className="px-1.5 py-px" type="button" onClick={() => inputRef.current?.click?.()}>
              <input
                ref={inputRef}
                type="file"
                accept={`.${props.fileExtension}`}
                style={{ display: 'none' }}
                onChange={upload}
              />
              <FontAwesomeIcon icon={faUpload} />
            </button>
          </Tooltip>
        )}
        <Tooltip title="Copy to clipboard">
          <button className="px-1.5 py-px" type="button" onClick={copy}>
            <FontAwesomeIcon icon={faCopy} />
          </button>
        </Tooltip>
        {editable && (
          <Tooltip title="Paste from clipboard">
            <button className="px-1.5 py-px" type="button" onClick={paste}>
              <FontAwesomeIcon icon={faPaste} />
            </button>
          </Tooltip>
        )}
        <Tooltip
          title={expanded ? 'Collapse' : 'Expand'}
          onClick={() => setExpanded((prevExpanded) => !prevExpanded)}>
          <button className="px-1.5 py-px" type="button">
            <FontAwesomeIcon icon={expanded ? faDownLeftAndUpRightToCenter : faUpRightAndDownLeftFromCenter} />
          </button>
        </Tooltip>
      </div>
    </div>
  )
}
